import React from 'react';
import styled from 'styled-components';
import PageLayout from './PageLayout';
import theme, { mobileBreakpoint } from '../theme';
import { useRef, useState, useEffect } from 'react';
import { StarBadgeWithIcon } from './shared/CommonElements';
import { LESSON_CONFIG } from '../config/lessonConfig';

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  white-space: normal;
  word-break: break-word;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 16px 64px 16px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  color: ${theme.colors.primary};
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.primary};
  font-weight: 600;
  margin: 32px 0 16px 0;
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
  margin-top: -5px;
  white-space: normal;
  word-break: break-word;
`;

const SectionNav = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 32px;
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
`;

const SegmentedButton = styled.button`
  padding: 10px 22px;
  border: 2px solid ${theme.colors.accent1};
  background: #fff;
  color: ${theme.colors.primary};
  font-weight: 600;
  border-radius: 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
  z-index: 1000;
  box-shadow: '0 2px 8px rgba(219, 39, 98, 0.08)';
  &:hover {
    background: ${({ theme }) => theme.colors.accent1};
    color: white;
  }
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
  }
`;

const AGBLink = styled.a`
  color: ${theme.colors.secondary};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
`;

const AGB = () => {
  const [activeSection, setActiveSection] = useState('agb');

  // Scroll to section on button click
  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Highlight active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const agbSection = document.getElementById('agb');
      const privacySection = document.getElementById('privacy');
      const widerrufSection = document.getElementById('widerruf');
      const impressumSection = document.getElementById('impressum');
      if (!agbSection || !privacySection) return;
      const scrollY = window.scrollY + 120; // offset for header
      if (impressumSection && impressumSection.offsetTop <= scrollY) {
        setActiveSection('impressum');
      } else if (widerrufSection && widerrufSection.offsetTop <= scrollY) {
        setActiveSection('widerruf');
      } else if (privacySection.offsetTop <= scrollY) {
        setActiveSection('privacy');
      } else {
        setActiveSection('agb');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Scroll to hash section on mount if present
    if (window.location.hash) {
      const hash = window.location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveSection(hash);
        }
      }, 100); // Delay to ensure DOM is ready
    }
  }, []);

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <StarBadgeWithIcon />
          <MainTitle>Rechtliches</MainTitle>
        </HeroTitle>
        <Subtitle>AGB, Datenschutzerklärung, Widerrufsbelehrung & Impressum</Subtitle>
      </HeroSection>
      <SectionNav>
        <SegmentedButton
          as="button"
          type="button"
          active={activeSection === 'agb'}
          onClick={() => handleSectionClick('agb')}
        >
          AGB
        </SegmentedButton>
        <SegmentedButton
          as="button"
          type="button"
          active={activeSection === 'privacy'}
          onClick={() => handleSectionClick('privacy')}
        >
          Datenschutzerklärung
        </SegmentedButton>
        <SegmentedButton
          as="button"
          type="button"
          active={activeSection === 'widerruf'}
          onClick={() => handleSectionClick('widerruf')}
        >
          Widerrufsbelehrung
        </SegmentedButton>
        <SegmentedButton
          as="button"
          type="button"
          active={activeSection === 'impressum'}
          onClick={() => handleSectionClick('impressum')}
        >
          Impressum
        </SegmentedButton>
      </SectionNav>
      <ContentContainer>
        <div id="agb">
          <Title>Allgemeine Geschäftsbedingungen (AGB)</Title>
          <SectionTitle>§ 1 Vertragsgegenstand</SectionTitle>
          <Paragraph>
            Gegenstand des Vertrags ist die Erbringung von Sprachunterricht (Englisch) in der vereinbarten Anzahl von Unterrichtseinheiten und Dauer.
          </Paragraph>
          <SectionTitle>§ 2 Buchung, Stundenbudget und Terminvereinbarung</SectionTitle>
          <Paragraph>
          <strong>Stundenpakete:</strong> Mit der Buchung eines Stundenpakets erwerben Sie ein Stundenbudget in Höhe der vereinbarten Anzahl von Unterrichtsstunden für den vereinbarten Stundentyp.
          Dieses Stundenbudget kann innerhalb eines Jahres ab Kaufdatum für die Buchung von Unterrichtsstunden dieses Stundentyps verwendet werden.
          Nach Ablauf dieser Frist verfällt das nicht genutzte Stundenbudget ersatzlos. Die Terminvereinbarung erfolgt individuell in Abstimmung mit uns über die <i>Neue Stunde buchen</i> Funktionalität auf der <i>Mein Bereich</i> Seite und ist nur in den verfügbaren Zeitfenstern möglich. Termine können pro gebuchter und bestätigter Stunde bis zu 3 Mal verschoben oder storniert werden. Unbestätigte Stunden können beliebig oft verschoben oder storniert werden.
          </Paragraph>
          <Paragraph>
            Wird eine bestätigte Stunde storniert, wird diese Stornierung der nächsten Stunde desselben Stundentyps als Verschiebung angerechnet. Hat eine bestätigte Stunde 3 Verschiebungen erreicht und wird storniert oder wird weniger als {LESSON_CONFIG.CANCELLATION_RESTRICTION_HOURS} Stunden vor dem vereinbarten Termin storniert, wird das Stundenbudget nicht zurückerstattet.
          </Paragraph>
          <Paragraph>
            <strong>Probestunden:</strong> Mit der Buchung einer Probestunde erwerben Sie eine Probestunde zum ausgewählten Termin. Wir werden diesen Termin innerhalb von 24 Stunden bestätigen. Sollten wir den Termin nicht innerhalb von 24 Stunden akzeptieren, wird der Kauf storniert und der Rechnungsbetrag erstattet. Die Rechnung erhalten Sie direkt nach der Terminbestätigung per E-Mail.
          </Paragraph>
          <SectionTitle>§ 3 Zahlungsbedingungen</SectionTitle>
          <Paragraph>
          Die Rechnung wird unmittelbar nach Vertragsschluss (oder im Falle einer Probestundenbuchung direkt nach der Terminbestätigung) ausgestellt und ist innerhalb von 14 Tagen nach Rechnungsdatum fällig. Die Zahlung erfolgt unabhängig davon, ob und wann die gebuchten Unterrichtsstunden in Anspruch genommen werden. Nicht genutzte Stunden verfallen nach Ablauf von 12 Monaten ab Vertragsschluss ersatzlos. Die Zahlung ist per Überweisung, SEPA-Lastschrift oder PayPal möglich.
          </Paragraph>
          <SectionTitle>§ 4 Widerrufsrecht</SectionTitle>
          <Paragraph>
            Verbraucher haben ein gesetzliches Widerrufsrecht von 14 Tagen ab Vertragsabschluss. Die Einzelheiten zum Widerrufsrecht, den Ausnahmen und den Folgen des Widerrufs entnehmen Sie bitte unserer gesonderten{' '}
            <AGBLink href="#widerruf" className="link">
              Widerrufsbelehrung
            </AGBLink>
            .
          </Paragraph>
          <SectionTitle>§ 5 Stornierung und Umbuchung</SectionTitle>
          <Paragraph>
            Stornierungen und Umbuchungen von gebuchten Stunden sind bis 24 Stunden vor dem vereinbarten Termin bis zu drei mal pro gebuchter Stunde kostenfrei möglich. Bei Stornierungen oder Umbuchungen weniger als 24 Stunden vor dem vereinbarten Termin, oder bei mehr als drei Verschiebeungen oder Stornierungen pro gebuchter Stunde wird das Stundenbudget nicht zurückerstattet.
          </Paragraph>
          <SectionTitle>§ 6 Haftung</SectionTitle>
          <Paragraph style={{marginBottom: '-20px'}}>
            Wir haften nicht für Nichterreichung bestimmter Lernziele. Die Haftung für leichte Fahrlässigkeit wird ausgeschlossen, soweit keine wesentlichen Vertragspflichten verletzt wurden.
          </Paragraph>
        </div>
      </ContentContainer>
      <ContentContainer style={{marginTop: 48}}>
        <div id="widerruf">
          <Title>Widerrufsbelehrung</Title>
          <SectionTitle>Widerrufsrecht:</SectionTitle>
          <Paragraph>
            Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen den Vertrag zu widerrufen.<br></br>
            Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.
          </Paragraph>
          <Paragraph>
            Um Ihr Widerrufsrecht auszuüben, müssen Sie uns mittels einer eindeutigen Erklärung (z. B. per "Probestunde stornieren" Link in der Bestätigungsmail, "Paket stornieren" Button auf der Website oder per E-Mail an <AGBLink href="mailto:mey@meyteaches.de">mey@meyteaches.de</AGBLink>) über Ihren Entschluss, den Vertrag zu widerrufen, informieren.<br></br>
          </Paragraph>
          <SectionTitle>Folgen des Widerrufs:</SectionTitle>
          <Paragraph>
            Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf bei uns eingegangen ist.<br></br>
            Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart.
          </Paragraph>
          <SectionTitle>Besonderer Hinweis:</SectionTitle>
          <Paragraph style={{marginBottom: '-20px'}}>
            Wenn Sie zugestimmt haben, dass wir die Dienstleistung (Unterricht) bereits vor Ablauf der Widerrufsfrist beginnen, und die Dienstleistung teilweise erbracht wurde, schulden Sie uns Wertersatz für die bis zu diesem Zeitpunkt erbrachten Leistungen.<br></br>
            Haben wir die Dienstleistung vollständig erbracht, bevor Sie Ihr Widerrufsrecht ausüben, erlischt dieses.
          </Paragraph>
        </div>
      </ContentContainer>
      <ContentContainer style={{marginTop: 48}}>
        <div id="privacy">
          <Title>Datenschutzerklärung</Title>
          <Paragraph>
            <strong>1. Verantwortlicher</strong><br />
            Michelle Köhler<br />
            Münchener Straße 2, 85748 Garching bei München<br />
            E-Mail: <AGBLink href="mailto:mey@meyteaches.de">mey@meyteaches.de</AGBLink>
          </Paragraph>
          <Paragraph>
            <strong>2. Erhebung und Speicherung personenbezogener Daten</strong><br />
            Wir erheben personenbezogene Daten (z. B. Name, E-Mail-Adresse, Rechnungsadresse) ausschließlich zur Abwicklung von Buchungen und zur Erfüllung des Unterrichtsvertrags.
          </Paragraph>
          <Paragraph>
            <strong>3. Zweck der Verarbeitung</strong><br />
            Ihre Daten werden verarbeitet, um:<br />
            <ul style={{marginLeft: 40}}>
              <li>Buchungen abzuwickeln</li>
              <li>Rechnungen zu erstellen</li>
              <li>Mit Ihnen zur Terminvereinbarung zu kommunizieren</li>
            </ul>
          </Paragraph>
          <Paragraph>
            <strong>4. Rechtsgrundlage</strong><br />
            Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).
          </Paragraph>
          <Paragraph>
            <strong>5. Speicherung</strong><br />
            Wir speichern Ihre Daten so lange, wie dies für die Vertragserfüllung erforderlich ist und gemäß steuerrechtlichen Aufbewahrungsfristen.
          </Paragraph>
          <Paragraph>
            <strong>6. Weitergabe an Dritte</strong><br />
            Eine Weitergabe erfolgt nur an Dienstleister, die für Zahlungsabwicklung (z. B. PayPal) oder Hosting notwendig sind. Diese sind vertraglich zur Einhaltung der DSGVO verpflichtet.
          </Paragraph>
          <Paragraph>
            <strong>7. Ihre Rechte</strong><br />
            Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung sowie das Recht auf Datenübertragbarkeit.<br />
            Sie können sich bei einer Aufsichtsbehörde beschweren, wenn Sie Verstöße gegen die DSGVO vermuten.
          </Paragraph>
          <Paragraph style={{marginBottom: '-20px'}}>
            <strong>8. Kontakt</strong><br />
            Für Fragen zum Datenschutz wenden Sie sich bitte an: <AGBLink href="mailto:mey@meyteaches.de">mey@meyteaches.de</AGBLink>
          </Paragraph>
        </div>
      </ContentContainer>
      <ContentContainer style={{marginTop: 48}}>
        <div id="impressum">
          <Title>Impressum</Title>
          <SectionTitle>Angaben gemäß § 5 TMG:</SectionTitle>
          <Paragraph>
            Michelle Köhler<br/>
            Münchener Straße 2<br/>
            85748 Garching bei München<br/>
            Deutschland
          </Paragraph>
          <SectionTitle>Kontakt:</SectionTitle>
          <Paragraph>
            E-Mail: <AGBLink href="mailto:mey@meyteaches.de">mey@meyteaches.de</AGBLink><br/>
          </Paragraph>
          <SectionTitle>Umsatzsteuer:</SectionTitle>
          <Paragraph>
            Kleinunternehmer gemäß § 19 UStG – keine Umsatzsteuer ausgewiesen.
          </Paragraph>
          <SectionTitle>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:</SectionTitle>
          <Paragraph>
            Michelle Köhler<br/>
            Münchener Straße 2<br/>
            85748 Garching bei München
          </Paragraph>
          <SectionTitle>Online-Streitbeilegung:</SectionTitle>
          <Paragraph>
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:<br/>
            <AGBLink href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr</AGBLink><br/>
            Unsere E-Mail-Adresse finden Sie oben im Impressum.
          </Paragraph>
          <SectionTitle>Haftung für Inhalte:</SectionTitle>
          <Paragraph>
            Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.<br/>
          </Paragraph>
          <SectionTitle>Urheberrecht:</SectionTitle>
          <Paragraph style={{marginBottom: '-20px'}}>
            Die Inhalte dieser Seite sind urheberrechtlich geschützt. Eine Vervielfältigung, Bearbeitung oder Verbreitung ist ohne schriftliche Zustimmung nicht gestattet.
          </Paragraph>
        </div>
      </ContentContainer>
    </PageLayout>
  );
};

export default AGB; 